// src/services/thumbnailService.js

const DB_NAME = 'MyGymTV_Thumbnails';
const DB_VERSION = 1;
const STORE_NAME = 'thumbnails';

class ThumbnailService {
    constructor() {
        this.db = null;
        this.dbReady = this.initDB();
    }

    /**
     * Initialize IndexedDB
     */
    async initDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('Failed to open IndexedDB:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'contentId' });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }

    /**
     * Get a thumbnail from the local cache
     */
    async getFromCache(contentId) {
        await this.dbReady;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(contentId);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                const result = request.result;
                if (result && result.blob) {
                    resolve(URL.createObjectURL(result.blob));
                } else {
                    resolve(null);
                }
            };
        });
    }

    /**
     * Save a thumbnail to the local cache
     */
    async saveToCache(contentId, blob) {
        await this.dbReady;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const request = store.put({
                contentId,
                blob,
                timestamp: Date.now()
            });

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Remove a thumbnail from the cache
     */
    async removeFromCache(contentId) {
        await this.dbReady;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(contentId);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Clear all cached thumbnails
     */
    async clearCache() {
        await this.dbReady;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /**
     * Get thumbnail - checks cache first, then fetches from server
     */
    async getThumbnail(contentId) {
        // Try cache first
        const cached = await this.getFromCache(contentId);
        if (cached) {
            return cached;
        }

        // Fetch from server
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentId}/thumbnail`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch thumbnail: ${response.status}`);
            }

            const blob = await response.blob();

            // Save to cache
            await this.saveToCache(contentId, blob);

            return URL.createObjectURL(blob);
        } catch (error) {
            console.error(`Error fetching thumbnail for content ${contentId}:`, error);
            return null;
        }
    }

    /**
     * Batch generate thumbnails on the server
     * Called on initial load to ensure all thumbnails exist
     */
    async batchGenerateThumbnails(contentIds) {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/thumbnails/batch`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(contentIds)
                }
            );

            if (!response.ok) {
                throw new Error(`Batch generation failed: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error in batch thumbnail generation:', error);
            throw error;
        }
    }

    /**
     * Preload thumbnails into cache
     * Fetches and caches thumbnails for all provided content IDs
     */
    async preloadThumbnails(contentIds, onProgress = null) {
        const results = {
            loaded: 0,
            failed: 0,
            cached: 0
        };

        for (let i = 0; i < contentIds.length; i++) {
            const contentId = contentIds[i];

            try {
                // Check if already cached
                const cached = await this.getFromCache(contentId);
                if (cached) {
                    results.cached++;
                    URL.revokeObjectURL(cached); // Clean up the blob URL
                } else {
                    // Fetch and cache
                    const url = await this.getThumbnail(contentId);
                    if (url) {
                        results.loaded++;
                        URL.revokeObjectURL(url); // Clean up the blob URL
                    } else {
                        results.failed++;
                    }
                }
            } catch (error) {
                results.failed++;
            }

            // Report progress
            if (onProgress) {
                onProgress({
                    current: i + 1,
                    total: contentIds.length,
                    ...results
                });
            }
        }

        return results;
    }

    /**
     * Get cache statistics
     */
    async getCacheStats() {
        await this.dbReady;

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const countRequest = store.count();

            countRequest.onerror = () => reject(countRequest.error);
            countRequest.onsuccess = () => {
                resolve({
                    count: countRequest.result
                });
            };
        });
    }
}

// Export singleton instance
const thumbnailService = new ThumbnailService();
export default thumbnailService;